// ZoneBands - one zone of one level, exploded by height band (guide 9.5 and
// 13): below slab, floor and walls, ceiling plane, telecom band, ductwork,
// piping. Each band is a plate lifted above the last, so what stacks above
// what can be read at a glance. The hover panel and the zone page both
// draw it; `detailed` adds room numbers, keynote tags and equipment tags.
import React, { useMemo } from "react";
import {
  CATEGORY_FILL, ROOF, STATUS_COLOR, bounds, bucketKey, dotsPath, drawnSystems, isCovered, levelLabel, linesPath, marksPath,
  polysPath, systemColor,
} from "./glanceModel";

const DEFAULT_BANDS = [
  { tier: -1, name: "Below slab" }, { tier: 0, name: "Floor and walls" }, { tier: 1, name: "Ceiling plane" },
  { tier: 2, name: "Telecom band" }, { tier: 3, name: "Ductwork" }, { tier: 4, name: "Piping" }, { tier: 5, name: "Roof" },
];

export default function ZoneBands({ manifest, level, zone, active, statuses, mode, items, detailed = false, width = 1140 }) {
  const z = (manifest.zones || [])[zone];
  const b = useMemo(() => bounds(manifest.levels || []), [manifest]);
  if (!z) return null;
  const isRoof = level === ROOF;
  const systems = drawnSystems(manifest).filter((s) => (!active || active.has(s.id)) && (isRoof ? s.id === ROOF : s.id !== ROOF));
  const bands = (manifest.bands && manifest.bands.length ? manifest.bands : DEFAULT_BANDS).slice().sort((p, q) => p.tier - q.tier);
  const tiers = isRoof ? [{ tier: 0, name: "Roof" }] : bands.filter((t) => t.tier === 0 || systems.some((s) => s.tier === t.tier));

  const ymin = b.ymin - 4; const ymax = b.ymax + 4;
  const x0 = Math.max(z.x0, b.xmin - 8); const x1 = Math.min(z.x1, b.xmax + 8);
  const k = 0.12; const left = detailed ? 250 : 230;
  const sc = Math.min((width - left - 40) / ((x1 - x0) + (ymax - ymin) * k * 0.55), detailed ? 11 : 9);
  const depth = (ymax - ymin) * sc * k;
  const positive = tiers.filter((t) => t.tier >= 0);
  // Guide 23.2: the hover panel's bands sit 92 apart; the zone page's
  // min(140, (820 - plate depth) / 5.6). The drawing scales to its box (58vh).
  const gap = detailed ? Math.max(48, Math.min(140, (820 - depth) / 5.6)) : 92;   // 48: a very deep plate never folds the bands
  const liftOf = (tier) => (isRoof ? 0 : tier < 0 ? -gap * 0.5 : positive.findIndex((t) => t.tier === tier) * gap);
  const maxLift = Math.max(0, ...tiers.map((t) => liftOf(t.tier)));
  const hasBelow = tiers.some((t) => t.tier < 0);
  const top = 28;
  const baseY = top + maxLift + depth;
  const height = Math.ceil(baseY + (hasBelow ? gap * 0.5 : 0) + 30);
  const Q = (x, y, lift) => [left + (x - x0) * sc + (y - ymin) * sc * k * 0.55, baseY - (y - ymin) * sc * k - lift];
  const clampX = (x) => Math.min(Math.max(x, x0), x1);
  const corners = (lift) => [[x0, ymin], [x1, ymin], [x1, ymax], [x0, ymax]].map((c) => Q(c[0], c[1], lift));
  const pts = (arr) => arr.map((c) => `${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(" ");
  const lvl = (manifest.levels || []).find((l) => l.id === level);
  const zoneItems = (items || []);

  const notReceived = systems.filter((s) => !isCovered(s, level, zone)).map((s) => s.label || s.id);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${levelLabel(manifest, level)}, ${z.label}, exploded by height`}>
      {!isRoof && corners(0).map((c, i) => {
        const lo = corners(hasBelow ? liftOf(-1) : 0)[i]; const hi = corners(maxLift)[i];
        return <line key={i} x1={lo[0]} y1={lo[1]} x2={hi[0]} y2={hi[1]} stroke="var(--line)" strokeWidth="0.6" strokeDasharray="2 4" />;
      })}
      {tiers.map((t) => {
        const lift = liftOf(t.tier);
        const c = corners(lift);
        const edgeMid = [(c[0][0] + c[3][0]) / 2, (c[0][1] + c[3][1]) / 2];
        const proj = (x, y) => Q(x, y, lift);
        const inTier = isRoof ? systems : systems.filter((s) => s.tier === t.tier);
        return (
          <g key={t.tier}>
            <polygon points={pts(c)} fill={t.tier === 0 || isRoof ? (isRoof ? "var(--roof)" : "var(--sheet)") : "none"} stroke="var(--line)" strokeWidth="0.8" strokeDasharray={t.tier === 0 || isRoof ? undefined : "4 4"} />
            <line x1={c[0][0]} y1={c[0][1]} x2={c[3][0]} y2={c[3][1]} stroke="var(--ink)" strokeWidth="1.4" />
            <line x1={edgeMid[0] - 14} y1={edgeMid[1]} x2={edgeMid[0]} y2={edgeMid[1]} stroke="var(--ink2)" />
            <text x={edgeMid[0] - 18} y={edgeMid[1] + 5} textAnchor="end" fontSize={detailed ? 17 : 16} fontWeight="500" fill="var(--ink)">{t.name}</text>
            {t.tier === 0 && !isRoof && lvl && lvl.rooms.map((r) => {
              let cx = 0; r.polygon.forEach((q) => { cx += q[0]; }); cx /= r.polygon.length;
              if (cx < z.x0 || cx >= z.x1) return null;
              return (
                <g key={r.id}>
                  <polygon points={pts(r.polygon.map((q) => proj(clampX(q[0]), q[1])))} fill={CATEGORY_FILL[r.category] || "var(--circ)"} fillOpacity="0.6" stroke="var(--line)" strokeWidth="0.4" />
                  {detailed && r.number && (r.areaSf || 0) > 60 && (() => {
                    let cy = 0; r.polygon.forEach((q) => { cy += q[1]; }); cy /= r.polygon.length;
                    const p = proj(Math.min(Math.max(cx, x0 + 6), x1 - 6), cy);
                    return <text x={p[0]} y={p[1]} textAnchor="middle" fontSize="12" fill="var(--ink2)" style={{ pointerEvents: "none" }}>{r.number}</text>;
                  })()}
                </g>
              );
            })}
            {inTier.map((s) => {
              const bucket = (manifest.buckets || {})[bucketKey(level, s.id, zone)];
              if (!bucket) return null;
              const st = mode === "progress" && statuses ? statuses[bucketKey(level, s.id, zone)] : null;
              const color = st ? STATUS_COLOR[st.st] : systemColor(s);
              const faded = mode === "progress" && !st;
              return (
                <g key={s.id} opacity={faded ? 0.3 : 1}>
                  {bucket.lines && <path d={linesPath(bucket.lines, proj)} stroke={color} strokeWidth={s.id === "ducts" ? 0.9 : 1.2} fill="none" strokeDasharray={s.dashed ? "4 3" : undefined} />}
                  {bucket.polys && <path d={polysPath(bucket.polys, proj)} fill={color} stroke="var(--systemink)" strokeWidth="0.5" />}
                  {bucket.dots && <path d={dotsPath(bucket.dots, proj, detailed ? 2.6 : 2)} fill={color} />}
                  {bucket.marks && <path d={marksPath(bucket.marks, (x, y) => Q(x, y, lift + 2), detailed ? 6 : 5)} fill={color} stroke="var(--sheet)" />}
                  {bucket.marks && (detailed || isRoof) && bucket.marks.filter((m) => m[2] && !/raceway|transformer|switchboard|transfer|^GB$/i.test(m[2])).slice(0, detailed ? 30 : 14).map((m, i) => {
                    const p = Q(m[0], m[1], lift + 8);
                    return <text key={i} x={p[0] + 6} y={p[1] - 4} fontSize="13" fill="var(--ink)" paintOrder="stroke" stroke="var(--sheet)" strokeWidth="3">{String(m[2]).replace(" (relocated)", "")}</text>;
                  })}
                </g>
              );
            })}
            {detailed && t.tier === 2 && zoneItems.filter((i) => i.c === "Keynoted pathways").map((i, n) => {
              const p = proj(i.x, i.y);
              return (
                <g key={n}>
                  <circle cx={p[0]} cy={p[1]} r="7" fill="var(--sheet)" stroke="var(--ink)" strokeWidth="0.8" />
                  <text x={p[0]} y={p[1] + 4} textAnchor="middle" fontSize="10" fill="var(--ink)">{String(i.tag).replace(/^KN\s*/i, "")}</text>
                </g>
              );
            })}
            {detailed && t.tier === 3 && zoneItems.filter((i) => i.c === "Equipment tags").map((i, n) => {
              const p = proj(i.x, i.y);
              return <text key={n} x={p[0]} y={p[1] - 6} fontSize="12" fill="var(--ink)" paintOrder="stroke" stroke="var(--sheet)" strokeWidth="3">{i.tag}</text>;
            })}
          </g>
        );
      })}
      {notReceived.length > 0 && (
        <text x={width - 10} y={height - 8} textAnchor="end" fontSize="13" fill="var(--ink2)">
          {`${notReceived.join(", ")}: not in the received drawings for this zone`}
        </text>
      )}
    </svg>
  );
}
